/* eslint-disable */
/**
 * Nominate a Space — MEMBER nomination flow.
 *
 * Session 60.5 · This is the member-side path (not partner onboarding).
 * Members recommend a workspace, café, or local venue they think Métier
 * should add. The action is a mailto-based submission so the founding
 * team can triage without a full CRM. Kept explicitly distinct from
 * `/partners` (which is the owner/operator listing path).
 *
 * Click submission is tracked separately from the nav click so we can
 * measure funnel drop-off (nav click → submit).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import { trackNavCta } from "../lib/navCta";
import { useAuth } from "../lib/auth";

function buildMailto(form) {
  const subject = `Métier · Nominate a Space${form.name ? `: ${form.name}` : ""}`;
  const body = [
    "Hi Métier team,",
    "",
    `I'd like to nominate the following place to be added to Métier.`,
    "",
    `Name of place: ${form.name || "(name)"}`,
    `Location (city, state): ${form.city || "(city, state)"}`,
    `Type: ${form.type || "(workspace / café / food spot / event venue / other)"}`,
    `Website or link: ${form.link || "(url)"}`,
    "",
    "Why it belongs on Métier:",
    (form.reason || "(a sentence about what makes this a great work-friendly spot)"),
    "",
    `Nominated by: ${form.nominatedBy || "(your name)"}`,
  ].join("\n");
  return `mailto:contact@workmetier.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function NominateSpace() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "", city: "", type: "café", link: "", reason: "",
    nominatedBy: user?.name || "",
  });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit = form.name.trim().length > 1 && form.city.trim().length > 1;

  return (
    <AppShell>
      <div className="m-page-pad" style={{ maxWidth: 820, margin: "0 auto" }}>
        <span className="m-eyebrow">Nominate a Space</span>
        <h1 className="m-h1">Know a great <em>café, food, or work-friendly spot</em>?</h1>
        <p className="m-lede" style={{ marginBottom: 24 }}>
          Métier is community-curated. Whether it&#39;s a workspace to book, a
          café you&#39;d use your perks at, a lunch spot near work, or an event
          venue you love — tell us. We&#39;ll reach out and invite them to join.
        </p>

        <div
          data-testid="nominate-form"
          className="m-card"
          style={{ padding: "26px 28px", display: "grid", gap: 16 }}
        >
          <label className="m-field">
            <span>Name of the place</span>
            <input
              value={form.name}
              onChange={update("name")}
              placeholder="e.g. Little Amps Coffee"
              data-testid="nominate-name"
            />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <label className="m-field">
              <span>City, state</span>
              <input
                value={form.city}
                onChange={update("city")}
                placeholder="Harrisburg, PA"
                data-testid="nominate-city"
              />
            </label>
            <label className="m-field">
              <span>Type</span>
              <select value={form.type} onChange={update("type")} data-testid="nominate-type">
                <option value="café">Café</option>
                <option value="workspace">Workspace / coworking</option>
                <option value="event venue">Event venue</option>
                <option value="food spot">Food spot</option>
                <option value="other">Other local venue</option>
              </select>
            </label>
          </div>
          <label className="m-field">
            <span>Website or link (optional)</span>
            <input
              value={form.link}
              onChange={update("link")}
              placeholder="https://…"
              data-testid="nominate-link"
            />
          </label>
          <label className="m-field">
            <span>Why does it belong on Métier?</span>
            <textarea
              value={form.reason}
              onChange={update("reason")}
              rows={4}
              placeholder="Great wifi, quiet at 3pm, actual power outlets, staff won't kick you out after one drink…"
              data-testid="nominate-reason"
            />
          </label>
          <label className="m-field">
            <span>Nominated by (your name)</span>
            <input
              value={form.nominatedBy}
              onChange={update("nominatedBy")}
              placeholder="Your name"
              data-testid="nominate-by"
            />
          </label>
          <div>
            <a
              href={canSubmit ? buildMailto(form) : "#"}
              onClick={(e) => {
                if (!canSubmit) { e.preventDefault(); return; }
                trackNavCta("nominate_submit");
              }}
              className="m-btn-gold"
              data-testid="nominate-submit"
              style={{
                display: "inline-block",
                opacity: canSubmit ? 1 : 0.55,
                pointerEvents: canSubmit ? "auto" : "none",
              }}
            >
              Send my nomination &rarr;
            </a>
            <p style={{ fontSize: 12, opacity: 0.7, marginTop: 10 }}>
              Opens your email app with a pre-filled note to the Métier team.
              We reply to every nomination.
            </p>
          </div>
        </div>

        <p style={{ marginTop: 24, fontSize: 14, opacity: 0.75 }}>
          Own or operate this place yourself?{" "}
          <Link to="/partners" style={{ color: "var(--gold)", textDecoration: "none", fontWeight: 600 }}>
            List it directly on the For Partners page &rarr;
          </Link>
        </p>
      </div>
    </AppShell>
  );
}
